import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Activity, 
  TrendingUp, 
  Calendar,
  Target,
  LogOut,
  Settings,
  ChevronRight,
  Flame,
  Moon,
  Zap,
  Ruler
} from 'lucide-react';
import { FigIcon } from '../components/FigIcon';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [plan, setPlan] = useState(null);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, planRes, scoreRes] = await Promise.allSettled([
        api.get('/dashboard/stats'),
        api.get('/plan/current'),
        api.get('/score/latest')
      ]);

      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (planRes.status === 'fulfilled') setPlan(planRes.value.data);
      if (scoreRes.status === 'fulfilled') setScore(scoreRes.value.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#EBEBE6] border border-[#D1D1CD] rounded-lg p-3 shadow-lg">
          <p className="text-sm text-[#525252]">{label}</p>
          <p className="text-lg font-bold">{payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8E4DC] flex items-center justify-center">
        <Activity className="w-12 h-12 text-[#2F3E30] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FigIcon className="w-6 h-6 text-[#2F3E30]" />
            <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
          </Link>
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <Button 
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="text-[#F59E0B] hover:text-[#1A1C19]"
                data-testid="admin-btn"
              >
                Admin
              </Button>
            )}
            <Button 
              variant="ghost" 
              onClick={() => navigate('/settings')}
              className="text-[#525252] hover:text-[#1A1C19]"
              data-testid="settings-btn"
            >
              <Settings className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-[#525252] hover:text-[#1A1C19]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Welcome */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              {user?.name ? `WELCOME BACK, ${user.name.toUpperCase()}` : 'YOUR DASHBOARD'}
            </h1>
            <p className="text-[#525252]">Track your metabolic progress</p>
          </div>

          {/* Quick Actions */}
          {!user?.questionnaire_completed && (
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 mb-8 border-[#F59E0B]/30">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>COMPLETE YOUR ASSESSMENT</h3>
                  <p className="text-sm text-[#525252]">Get your metabolic trajectory score</p>
                </div>
                <Button 
                  onClick={() => navigate('/questionnaire')}
                  className="bg-[#F59E0B] hover:bg-[#D97706]"
                  data-testid="complete-assessment-btn"
                >
                  Start Now
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* Score */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#2F3E30]/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#2F3E30]" />
                </div>
                <span className="text-sm text-[#525252]">Risk Score</span>
              </div>
              <p className="text-3xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                {score ? Math.round(score.total_score) : '--'}
              </p>
              {score && (
                <p className="text-sm" style={{ 
                  color: score.risk_tier === 'Low' ? '#10B981' : score.risk_tier === 'Moderate' ? '#F59E0B' : '#EF4444'
                }}>
                  {score.risk_tier} Risk
                </p>
              )}
            </div>

            {/* Streak */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <span className="text-sm text-[#525252]">Streak</span>
              </div>
              <p className="text-3xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                {stats?.adherence_streak || 0}
              </p>
              <p className="text-sm text-[#525252]">weeks</p>
            </div>

            {/* Check-ins */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#10B981]" />
                </div>
                <span className="text-sm text-[#525252]">Check-ins</span>
              </div>
              <p className="text-3xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                {stats?.total_checkins || 0}
              </p>
              <p className="text-sm text-[#525252]">total</p>
            </div>

            {/* Active Rules */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-[#8B5CF6]" />
                </div>
                <span className="text-sm text-[#525252]">Active Rules</span>
              </div>
              <p className="text-3xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                {plan?.rules?.length || 0}
              </p>
              <p className="text-sm text-[#525252]">in plan</p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Waist Chart */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Ruler className="w-5 h-5 text-[#2F3E30]" />
                  <h3 className="font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>WAIST OVER TIME</h3>
                </div>
              </div>
              {stats?.waist_history?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={stats.waist_history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#D1D1CD" />
                    <XAxis dataKey="date" stroke="#525252" fontSize={12} />
                    <YAxis stroke="#525252" fontSize={12} domain={['dataMin - 2', 'dataMax + 2']} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#2F3E30" 
                      strokeWidth={2}
                      dot={{ fill: '#2F3E30', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-[#525252]">
                  No data yet. Complete a check-in to see your progress.
                </div>
              )}
            </div>

            {/* Energy Chart */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-[#F59E0B]" />
                  <h3 className="font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>ENERGY OVER TIME</h3>
                </div>
              </div>
              {stats?.energy_history?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={stats.energy_history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#D1D1CD" />
                    <XAxis dataKey="date" stroke="#525252" fontSize={12} />
                    <YAxis stroke="#525252" fontSize={12} domain={[1, 5]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#F59E0B" 
                      strokeWidth={2}
                      dot={{ fill: '#F59E0B', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-[#525252]">
                  No data yet. Complete a check-in to track energy.
                </div>
              )}
            </div>

            {/* Sleep Chart */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Moon className="w-5 h-5 text-[#8B5CF6]" />
                  <h3 className="font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>SLEEP QUALITY</h3>
                </div>
              </div>
              {stats?.sleep_history?.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={stats.sleep_history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#D1D1CD" />
                    <XAxis dataKey="date" stroke="#525252" fontSize={12} />
                    <YAxis stroke="#525252" fontSize={12} domain={[1, 5]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#8B5CF6" 
                      strokeWidth={2}
                      dot={{ fill: '#8B5CF6', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-[#525252]">
                  No data yet. Complete a check-in to track sleep.
                </div>
              )}
            </div>

            {/* Current Plan */}
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-[#10B981]" />
                  <h3 className="font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>CURRENT RULES</h3>
                </div>
                {plan && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/plan')}
                    className="text-[#2F3E30]"
                  >
                    View Plan
                  </Button>
                )}
              </div>
              {plan?.rules?.length > 0 ? (
                <div className="space-y-3">
                  {plan.rules.slice(0, 4).map((rule, index) => (
                    <div key={rule.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#E8E4DC]">
                      <div className="w-6 h-6 rounded-full bg-[#D1D1CD] flex items-center justify-center text-xs">
                        {index + 1}
                      </div>
                      <span className="text-sm">{rule.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[200px] flex flex-col items-center justify-center text-[#525252]">
                  <p className="mb-4">No active plan</p>
                  {user?.is_paid && (
                    <Button onClick={() => navigate('/plan')} size="sm" className="bg-[#2F3E30] text-white">
                      Generate Plan
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-4 justify-center">
            {user?.questionnaire_completed && !user?.is_paid && (
              <Button 
                onClick={() => navigate('/results')}
                className="bg-[#2F3E30] hover:bg-[#3A4D3B]"
                data-testid="view-results-btn"
              >
                View My Results
              </Button>
            )}
            {user?.is_paid && (
              <>
                <Button 
                  onClick={() => navigate('/checkin')}
                  className="bg-[#2F3E30] hover:bg-[#3A4D3B]"
                  data-testid="weekly-checkin-btn"
                >
                  Weekly Check-in
                </Button>
                <Button 
                  onClick={() => navigate('/plan')}
                  variant="outline"
                  className="border-[#D1D1CD]"
                  data-testid="view-plan-btn"
                >
                  View Full Plan
                </Button>
              </>
            )}
            {!user?.is_paid && user?.questionnaire_completed && (
              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-[#10B981] hover:bg-[#059669]"
                data-testid="unlock-plan-btn"
              >
                Unlock Your Plan
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
